import { createFileRoute } from '@tanstack/react-router';
import * as React from 'react';

import { PageHeader } from '@/components/dashboard/page-header';
import { DataTable, TableTag, type DataTableColumn } from '@/components/data-table';
import { trpc } from '@/lib/trpc/client';

export const Route = createFileRoute('/_admin/admin/grants')({
  component: GrantsPage,
});

const PLANS = ['FREE', 'PLUS', 'PREMIUM', 'PRO', 'BUSINESS'] as const;
type PlanValue = (typeof PLANS)[number];

interface GrantRow {
  id: string;
  userId: string;
  plan: string;
  startsAt: Date;
  endsAt: Date | null;
  reason: string;
  grantedById: string | null;
  createdAt: Date;
  user: { email: string; displayName: string | null };
}

function GrantsPage(): React.ReactElement {
  const utils = trpc.useUtils();
  const list = trpc.admin.grantList.useQuery({ limit: 50 });
  const rows = (list.data ?? []) as GrantRow[];

  const [form, setForm] = React.useState({
    userId: '',
    plan: 'PLUS' as PlanValue,
    startsAt: '',
    endsAt: '',
    reason: '',
  });
  const [showForm, setShowForm] = React.useState(false);

  const grantPlan = trpc.admin.grantPlan.useMutation({
    onSuccess: () => {
      setShowForm(false);
      setForm({ userId: '', plan: 'PLUS', startsAt: '', endsAt: '', reason: '' });
      void utils.admin.grantList.invalidate();
    },
  });

  const revoke = trpc.admin.revokePlanGrant.useMutation({
    onSuccess: () => {
      void utils.admin.grantList.invalidate();
    },
  });

  const columns: DataTableColumn<GrantRow>[] = [
    {
      key: 'user',
      header: 'Compte',
      cell: (row) => <span className="font-medium">{row.user.email}</span>,
    },
    { key: 'plan', header: 'Plan', cell: (row) => <TableTag label={row.plan} /> },
    {
      key: 'period',
      header: 'Période',
      cell: (row) => (
        <span className="text-muted-foreground text-xs">
          {new Date(row.startsAt).toLocaleDateString('fr-FR')}
          {row.endsAt ? ` → ${new Date(row.endsAt).toLocaleDateString('fr-FR')}` : ' → ∞'}
        </span>
      ),
    },
    {
      key: 'reason',
      header: 'Motif',
      cell: (row) => (
        <span className="text-muted-foreground line-clamp-1 max-w-xs text-xs">{row.reason}</span>
      ),
    },
  ];

  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-6">
      <PageHeader title="Plans octroyés" subtitle="Gestion des AdminPlanGrant." />
      <div className="space-y-5">
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => setShowForm((v) => !v)}
            className="bg-primary text-primary-foreground rounded-lg px-4 py-2 text-sm font-medium"
          >
            {showForm ? 'Annuler' : 'Octroyer un plan'}
          </button>
        </div>
        {showForm ? (
          <div className="bg-card border-border space-y-3 rounded-xl border p-5">
            <div className="grid gap-3 sm:grid-cols-2">
              <input
                aria-label="User ID"
                className="border-border bg-background rounded-lg border px-3 py-1.5 text-sm"
                placeholder="User ID (cuid)"
                value={form.userId}
                onChange={(e) => setForm((p) => ({ ...p, userId: e.target.value }))}
              />
              <select
                aria-label="Plan"
                className="border-border bg-background rounded-lg border px-3 py-1.5 text-sm"
                value={form.plan}
                onChange={(e) => setForm((p) => ({ ...p, plan: e.target.value as PlanValue }))}
              >
                {PLANS.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
              <input
                aria-label="Début"
                type="datetime-local"
                className="border-border bg-background rounded-lg border px-3 py-1.5 text-sm"
                value={form.startsAt}
                onChange={(e) => setForm((p) => ({ ...p, startsAt: e.target.value }))}
              />
              <input
                aria-label="Fin"
                type="datetime-local"
                className="border-border bg-background rounded-lg border px-3 py-1.5 text-sm"
                value={form.endsAt}
                onChange={(e) => setForm((p) => ({ ...p, endsAt: e.target.value }))}
              />
              <input
                aria-label="Motif"
                className="border-border bg-background rounded-lg border px-3 py-1.5 text-sm sm:col-span-2"
                placeholder="Motif (obligatoire)…"
                value={form.reason}
                onChange={(e) => setForm((p) => ({ ...p, reason: e.target.value }))}
              />
            </div>
            {grantPlan.error ? (
              <p className="text-destructive text-xs">{grantPlan.error.message}</p>
            ) : null}
            <button
              type="button"
              disabled={grantPlan.isPending || !form.userId || !form.startsAt || !form.reason}
              onClick={() =>
                grantPlan.mutate({
                  userId: form.userId,
                  plan: form.plan,
                  startsAt: new Date(form.startsAt).toISOString(),
                  endsAt: form.endsAt ? new Date(form.endsAt).toISOString() : undefined,
                  reason: form.reason,
                })
              }
              className="bg-primary text-primary-foreground rounded-lg px-4 py-1.5 text-sm font-medium disabled:opacity-50"
            >
              {grantPlan.isPending ? 'Création…' : 'Créer'}
            </button>
          </div>
        ) : null}
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(row) => row.id}
          actionHeader=""
          rowAction={(row) => (
            <button
              type="button"
              aria-label={`Révoquer le plan de ${row.user.email}`}
              disabled={revoke.isPending}
              onClick={() => revoke.mutate({ grantId: row.id })}
              className="text-destructive border-destructive/30 hover:bg-destructive/5 rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-50"
            >
              {revoke.isPending ? 'Révocation…' : 'Révoquer'}
            </button>
          )}
          emptyState={
            <p className="text-muted-foreground text-center text-sm">Aucun plan octroyé.</p>
          }
        />
      </div>
    </div>
  );
}
